import { createFileRoute, Link, useNavigate, useBlocker } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  BadgeCheck,
  Camera,
  CheckCircle2,
  Copy,
  CreditCard,
  Crown,
  Download,
  ExternalLink,
  Info,
  KeyRound,
  Loader2,
  LogOut,
  Mail,
  Receipt,
  ShieldAlert,
  Sparkles,
  User as UserIcon,
  Link2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { Header } from "@/components/Header";
import { CancelMembershipDialog } from "@/components/CancelMembershipDialog";
import { DropNotificationPreferences } from "@/components/DropNotificationPreferences";
import { AvatarEditor } from "@/components/AvatarEditor";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";

import { useAuth, useAuthActions } from "@/lib/auth";
import { useDevState } from "@/lib/dev-state";
import { useLibraryAccess } from "@/lib/use-library-access";
import { isLegacySubscriber } from "@/lib/legacy-membership";
import { getMembershipInclusionsSummary } from "@/lib/membership-summary";
import { useIsLocalDevHost } from "@/lib/dev-mode";
import {
  updateMyProfile,
  useUserInvoices,
  useMetaPrices,
  resolveLibraryOnlyPrice,
  type UserInvoice,
  type UserInvoicePaymentMethod,
  type UserInvoiceProcessor,
} from "@/lib/firebase";
import {
  callContinueSubscription,
  callRefreshTokenServer,
} from "@/lib/firebase/functions";
import {
  findActiveSubscription,
  formatRenewalDate,
  hasPendingLibraryOnlySwitch,
  isLibraryOnlySubscription,
  isSuperSubMember,
  resolveCurrentMembershipTier,
} from "@/lib/membership-tier";
import { VipTelegramConnectBanner } from "@/components/vip-chat/VipTelegramConnectBanner";
import { AccountGiveawaysCard } from "@/components/giveaways/AccountGiveawaysCard";
import { Skeleton } from "@/components/ui/skeleton";
import { toastError } from "@/lib/toast";
import { useModalParam } from "@/lib/url-modal";
import { cn } from "@/lib/utils";

/** Re-enable when giveaways has other site entry points besides direct URL. */
const SHOW_ACCOUNT_GIVEAWAYS = false;

export const Route = createFileRoute("/account")({
  head: () => ({
    meta: [
      { title: "Account — Real Pranks" },
      {
        name: "description",
        content: "Manage your Real Pranks profile, membership, payment information, and receipts.",
      },
      { property: "og:title", content: "Account — Real Pranks" },
      {
        property: "og:description",
        content: "Manage your Real Pranks profile and membership.",
      },
    ],
  }),
  component: AccountPage,
});

const T_SHIRT_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
/** Sentinel for Radix Select when no size is stored in Firestore yet. */
const T_SHIRT_UNSET = "__none__";
const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
] as const;

const PLAN_PRICE_USD = 10;

const unsavedInputClass =
  "ring-2 ring-amber-500/60 border-amber-500/60 bg-amber-500/5";

function formatDate(d: Date) {
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLongDate(d: Date) {
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function socialSignInTooltipBody(hasGoogle: boolean, hasApple: boolean): string {
  const names: string[] = [];
  if (hasGoogle) names.push("Google");
  if (hasApple) names.push("Apple");
  const list = names.join(" and ");
  return `You’re signed in with ${list} using social sign-in. That means ${names.length > 1 ? "those providers authenticate you" : `${list} authenticates you`} instead of a Real Pranks password. The email shown here may come from your ${names.length > 1 ? "provider profiles" : "provider profile"}. Changing your sign-in email isn’t available on this page for social accounts—use ${list} to manage your identity when you need to update email or security settings.`;
}

/** First letter for avatar fallback, or null when neither name nor email yields one. */
function profileAvatarInitial(name: string, email: string): string | null {
  const n = name.trim();
  if (n) return n.charAt(0).toUpperCase();
  const em = email.trim();
  if (em) return em.charAt(0).toUpperCase();
  return null;
}

function formatRelativeWeeks(weeks: number) {
  if (weeks < 4) return `${weeks} week${weeks === 1 ? "" : "s"}`;
  if (weeks < 52) {
    const months = Math.floor(weeks / 4.345);
    return `${months} month${months === 1 ? "" : "s"}`;
  }
  const years = Math.floor(weeks / 52);
  const remMonths = Math.floor((weeks - years * 52) / 4.345);
  return remMonths > 0
    ? `${years} year${years === 1 ? "" : "s"}, ${remMonths} mo`
    : `${years} year${years === 1 ? "" : "s"}`;
}

/**
 * Build a synthetic but realistic billing history from a subscribed-weeks
 * count. Used ONLY for the dev state switcher previews — when a user has the
 * dev switcher set to a simulated state, no real Stripe customer exists so we
 * fabricate matching invoices so designers can preview the UI. Real users
 * hit the `listUserInvoices` callable instead.
 *
 * Roughly every fourth synthetic row is marked as a PayPal/Braintree payment
 * so designers can preview the brand-icon styling without flipping the dev
 * switcher into a real Braintree-funded test account.
 */
function buildSyntheticBillingHistory(weeks: number): {
  invoices: BillingInvoice[];
  totalMonths: number;
} {
  const now = new Date();
  const months = Math.max(1, Math.ceil(weeks / 4.345));
  const visible = Math.min(months, 24);
  const invoices: BillingInvoice[] = Array.from({ length: visible }, (_, i) => {
    const d = new Date(now);
    d.setMonth(d.getMonth() - i);
    const isPayPal = i % 4 === 1;
    return {
      id: `INV-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}-${String(2841 + i)}`,
      number: `INV-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}-${String(2841 + i)}`,
      date: d,
      amountCents: PLAN_PRICE_USD * 100,
      currency: "usd",
      status: "paid",
      invoicePdfUrl: null,
      hostedInvoiceUrl: null,
      receiptUrl: null,
      description: "Monthly membership",
      processor: isPayPal ? "braintree" : "stripe",
      paymentMethod: isPayPal ? "paypal" : "card",
    };
  });
  return { invoices, totalMonths: months };
}

type BillingInvoice = {
  id: string;
  number: string;
  date: Date;
  amountCents: number;
  currency: string;
  status: "paid" | "open" | "void" | "uncollectible" | "draft";
  invoicePdfUrl: string | null;
  hostedInvoiceUrl: string | null;
  receiptUrl: string | null;
  description: string | null;
  processor: UserInvoiceProcessor;
  paymentMethod: UserInvoicePaymentMethod;
};

function invoiceFromResponse(inv: UserInvoice): BillingInvoice {
  return {
    id: inv.id,
    number: inv.number,
    date: new Date(inv.dateMs),
    amountCents: inv.amountCents,
    currency: inv.currency,
    status: inv.status,
    invoicePdfUrl: inv.invoicePdfUrl,
    hostedInvoiceUrl: inv.hostedInvoiceUrl,
    receiptUrl: inv.receiptUrl,
    description: inv.description,
    processor: inv.processor,
    paymentMethod: inv.paymentMethod,
  };
}

type LocalDevTokenClaims = {
  subscriptions?: unknown;
  videos?: unknown;
};

function LocalDevProfileDebug({
  userId,
  membershipType,
  subscriptions,
}: {
  userId: string;
  membershipType: "legacy" | "v2" | undefined;
  subscriptions: ReturnType<typeof useAuth>["subscriptions"];
}) {
  const [tokenClaims, setTokenClaims] = useState<LocalDevTokenClaims | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { getFirebaseAuth } = await import("@/lib/firebase/config");
      const currentUser = getFirebaseAuth().currentUser;
      if (!currentUser) {
        if (!cancelled) setTokenClaims(null);
        return;
      }
      const result = await currentUser.getIdTokenResult();
      if (cancelled) return;
      setTokenClaims({
        subscriptions: result.claims.subscriptions,
        videos: result.claims.videos,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, membershipType, subscriptions]);

  const activeStatuses =
    subscriptions?.subscriptions
      ?.filter((s) => s.status === "active")
      .map((s) => s.status) ?? [];

  const copyUserId = async () => {
    try {
      await navigator.clipboard.writeText(userId);
      toast.success("User ID copied");
    } catch {
      toast.error("Could not copy User ID");
    }
  };

  return (
    <div className="mt-4 w-full rounded-lg border border-dashed border-[oklch(0.62_0.19_250)]/50 bg-[oklch(0.62_0.19_250)]/5 p-3 text-left">
      <p className="text-[10px] font-bold tracking-[0.2em] text-[oklch(0.62_0.19_250)] uppercase">
        Developer mode (localhost)
      </p>
      <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2">
        <div className="min-w-0">
          <p className="text-[10px] font-bold tracking-wide text-muted-foreground uppercase">
            User ID
          </p>
          <p className="truncate font-mono text-xs">{userId}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => void copyUserId()}
        >
          <Copy className="h-3.5 w-3.5" />
          Copy
        </Button>
      </div>
      <dl className="mt-3 space-y-1.5 font-mono text-[11px] text-muted-foreground">
        <div className="flex justify-between gap-2">
          <dt>membershipType</dt>
          <dd className="text-foreground">{membershipType ?? "undefined"}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt>active subs</dt>
          <dd className="text-foreground">{activeStatuses.length}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt>token.subscriptions</dt>
          <dd className="break-all text-foreground">
            {tokenClaims
              ? JSON.stringify(tokenClaims.subscriptions ?? null)
              : "loading…"}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt>token.videos</dt>
          <dd className="break-all text-foreground">
            {tokenClaims ? JSON.stringify(tokenClaims.videos ?? null) : "loading…"}
          </dd>
        </div>
      </dl>
    </div>
  );
}

const CURRENCY_FORMATTER_CACHE = new Map<string, Intl.NumberFormat>();
function formatMoney(amountCents: number, currency: string) {
  const key = currency.toUpperCase();
  let fmt = CURRENCY_FORMATTER_CACHE.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: key,
    });
    CURRENCY_FORMATTER_CACHE.set(key, fmt);
  }
  return fmt.format(amountCents / 100);
}

function ProfileFieldSkeleton({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <Skeleton className="mt-1 h-10 w-full" />
    </div>
  );
}

function AccountPage() {
  const {
    user,
    signedIn,
    isSubscriber,
    profile,
    settings,
    subscriptions,
    membershipType,
    creators,
    hasPasswordAuth,
    primarySignInMethodLabel,
    signInMethods,
    pendingAccountMerge,
    loading: authLoading,
  } = useAuth();
  const {
    signOut,
    sendPasswordReset,
    sendCurrentUserEmailVerification,
    linkEmailPassword,
    requestEmailChange,
    completeDeferredMerge,
    clearAccountMerge,
  } = useAuthActions();
  const dev = useDevState();
  const navigate = useNavigate();
  const showLocalDevPanel = useIsLocalDevHost();

  const isProfileLoading =
    authLoading || (Boolean(user?.uid) && profile === null);

  const [avatar, setAvatar] = useState<string | null>(profile?.avatar ?? null);
  const [editorSrc, setEditorSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [instagram, setInstagram] = useState("");
  const [tShirt, setTShirt] = useState("");
  const [gender, setGender] = useState<string>("");

  type ProfileSnapshot = {
    name: string;
    phoneNumber: string;
    instagram: string;
    tShirt: string;
    gender: string;
  };
  const [originals, setOriginals] = useState<ProfileSnapshot>({
    name: "",
    phoneNumber: "",
    instagram: "",
    tShirt: "",
    gender: "",
  });

  useEffect(() => {
    if (!user?.uid) {
      if (dev.state !== "real") {
        const empty: ProfileSnapshot = {
          name: "",
          phoneNumber: "",
          instagram: "",
          tShirt: "",
          gender: "",
        };
        setName(empty.name);
        setPhoneNumber(empty.phoneNumber);
        setInstagram(empty.instagram);
        setTShirt(empty.tShirt);
        setGender(empty.gender);
        setOriginals(empty);
        setAvatar(null);
        return;
      }
      if (!signedIn) return;
    }

    const g = settings?.gender?.trim();
    const snap: ProfileSnapshot = {
      name: (profile?.name ?? user?.displayName ?? "").trim(),
      phoneNumber: settings?.phoneNumber?.trim() ?? "",
      instagram: settings?.instagram?.trim() ?? "",
      tShirt: settings?.tShirt?.trim() ?? "",
      gender: g === "male" || g === "female" ? g : "",
    };
    setName(snap.name);
    setPhoneNumber(snap.phoneNumber);
    setInstagram(snap.instagram);
    setTShirt(snap.tShirt);
    setGender(snap.gender);
    setOriginals(snap);
    setAvatar(user?.uid ? (profile?.avatar ?? null) : null);
  }, [
    dev.state,
    signedIn,
    profile?.name,
    profile?.avatar,
    user?.displayName,
    user?.uid,
    settings?.phoneNumber,
    settings?.instagram,
    settings?.tShirt,
    settings?.gender,
  ]);

  const dirtyFields = {
    name: name !== originals.name,
    phoneNumber: phoneNumber !== originals.phoneNumber,
    instagram: instagram !== originals.instagram,
    tShirt: tShirt !== originals.tShirt,
    gender: gender !== originals.gender,
  };
  const isDirty = Object.values(dirtyFields).some(Boolean);

  const [savingProfile, setSavingProfile] = useState(false);
  const [resettingPw, setResettingPw] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [linkingEmail, setLinkingEmail] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linkPassword, setLinkPassword] = useState("");
  const [linkPasswordConfirm, setLinkPasswordConfirm] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [mergingAccount, setMergingAccount] = useState(false);

  // Email verification: real users mirror Firebase, dev-state users are "verified"
  // when subscribed and "unverified" otherwise.
  const email = user?.email ?? "";
  const avatarDisplaySrc = (avatar ?? "").trim() || null;
  const profilePlaceholderInitial = profileAvatarInitial(name, email);
  const emailVerified = user?.emailVerified ?? (dev.isSubscribed ? true : dev.isSignedIn);
  const effectiveHasPasswordAuth = dev.state === "real" ? hasPasswordAuth : true;
  const effectivePrimarySignInMethodLabel =
    dev.state === "real" ? primarySignInMethodLabel : "Email & Password";

  const hasGoogle = dev.state === "real" && signInMethods.includes("google.com");
  const hasApple = dev.state === "real" && signInMethods.includes("apple.com");
  const usesSocialSignIn = hasGoogle || hasApple;

  // ===== Subscription / billing =====
  const subscribedWeeks = dev.subscribedWeeks ?? (isSubscriber ? 4 : 0);
  const subscribedSince = useMemo(() => {
    if (subscribedWeeks <= 0) return null;
    const d = new Date();
    d.setDate(d.getDate() - subscribedWeeks * 7);
    return d;
  }, [subscribedWeeks]);
  // Real users hit Stripe via the callable; dev-state previews use synthetic
  // data so designers can still see the "300 weeks subscribed" mocks. The
  // hook is disabled in dev-state mode so we never make a server round-trip
  // for users who aren't actually signed in to a real Firebase session.
  const isRealAuth = dev.state === "real";
  const { doc: pricesDoc, resolution: pricesResolution } = useMetaPrices();
  const activeSubscription = useMemo(() => findActiveSubscription(subscriptions), [subscriptions]);

  const nextRenewal = useMemo(() => {
    const renewsAt = activeSubscription?.renewsAt;
    if (renewsAt && typeof renewsAt.toDate === "function") {
      return renewsAt.toDate();
    }
    if (!isSubscriber && !dev.isSubscribed) return null;
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d;
  }, [activeSubscription?.renewsAt, isSubscriber, dev.isSubscribed]);

  const isCancellationScheduled =
    activeSubscription?.status === "active" && activeSubscription?.renewing === false;

  const isLibraryOnlyMember = isLibraryOnlySubscription(activeSubscription);
  const pendingLibraryOnlySwitch = hasPendingLibraryOnlySwitch(activeSubscription);

  const libraryOnlyPriceUsd = useMemo(
    () => resolveLibraryOnlyPrice(pricesDoc),
    [pricesDoc],
  );

  const planPriceUsd =
    isLibraryOnlyMember || pendingLibraryOnlySwitch
      ? libraryOnlyPriceUsd
      : (activeSubscription?.renewPrice ?? PLAN_PRICE_USD);

  const [resumingSubscription, setResumingSubscription] = useState(false);

  const currentMembership = useMemo(
    () => (pricesResolution?.ok ? resolveCurrentMembershipTier(activeSubscription, pricesResolution) : null),
    [activeSubscription, pricesResolution],
  );
  const isLegacyMembership = useMemo(
    () =>
      isLegacySubscriber({
        membershipType,
        currentMembership,
        devMembershipType: dev.membershipType,
        isDevMode: dev.state !== "real",
      }),
    [membershipType, currentMembership, dev.membershipType, dev.state],
  );
  const superSubAccess = isSuperSubMember(
    subscriptions,
    dev.state === "real" ? undefined : dev.tier,
  );
  const membershipLabel =
    currentMembership && !currentMembership.isLegacy
      ? `Real Pranks · ${currentMembership.tierName}${currentMembership.billing === "yearly" ? " · Yearly" : " · Monthly"}`
      : isSubscriber || dev.isSubscribed
        ? "Real Pranks · Monthly"
        : "Free";
  const membershipSummary = useMemo(
    () =>
      getMembershipInclusionsSummary({
        currentMembership,
        isLegacy: isLegacyMembership,
        creators,
        isSubscribed: isSubscriber,
        pendingLibraryOnlySwitch,
        devTier: dev.state !== "real" ? dev.tier : undefined,
        devMembershipType: dev.state !== "real" ? dev.membershipType : undefined,
        isDevSubscribed: dev.state !== "real" ? dev.isSubscribed : undefined,
      }),
    [
      currentMembership,
      isLegacyMembership,
      creators,
      isSubscriber,
      pendingLibraryOnlySwitch,
      dev.state,
      dev.tier,
      dev.membershipType,
      dev.isSubscribed,
    ],
  );
  const showBilling = (isRealAuth && isSubscriber) || (!isRealAuth && dev.isSubscribed);
  const {
    invoices: liveInvoices,
    loading: invoicesLoading,
    error: invoicesError,
    reload: reloadInvoices,
  } = useUserInvoices({
    enabled: isRealAuth && isSubscriber,
    limit: 24,
  });

  const billing = useMemo(() => {
    if (isRealAuth) {
      const invoices = (liveInvoices ?? []).map(invoiceFromResponse);
      return { invoices, totalMonths: invoices.length };
    }
    return buildSyntheticBillingHistory(subscribedWeeks);
  }, [isRealAuth, liveInvoices, subscribedWeeks]);

  const [billingExpanded, setBillingExpanded] = useState(false);
  const [billingPage, setBillingPage] = useState(0);

  // Cancellation flow: "offer" → discount retention, "confirm" → loss + archive offer.
  // Whether the flow is open lives in the URL (?modal=cancel-membership) so it
  // can be deep-linked and the back button dismisses it; the step within the
  // flow stays local.
  const [cancelFlowOpen, setCancelFlowOpen] = useModalParam("cancel-membership");
  const [cancelStep, setCancelStep] = useState<"closed" | "offer" | "confirm">("closed");
  useEffect(() => {
    if (cancelFlowOpen && cancelStep === "closed") {
      setCancelStep("offer");
    } else if (!cancelFlowOpen && cancelStep !== "closed") {
      setCancelStep("closed");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelFlowOpen]);
  const closeCancelFlow = () => {
    setCancelStep("closed");
    setCancelFlowOpen(false);
  };
  const { owned: devLibraryOwned } = useLibraryAccess();
  const videosOwned = devLibraryOwned.length;
  const BILLING_COLLAPSED = 3;
  const BILLING_PAGE_SIZE = 6;
  const billingVisible = billingExpanded
    ? billing.invoices.slice(
        billingPage * BILLING_PAGE_SIZE,
        billingPage * BILLING_PAGE_SIZE + BILLING_PAGE_SIZE,
      )
    : billing.invoices.slice(0, BILLING_COLLAPSED);
  const billingPageCount = Math.max(1, Math.ceil(billing.invoices.length / BILLING_PAGE_SIZE));
  const billingIsLoading = isRealAuth && invoicesLoading && !liveInvoices;
  const billingIsEmpty =
    isRealAuth && !invoicesLoading && !invoicesError && billing.invoices.length === 0;

  // ===== Handlers =====
  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setEditorSrc(url);
    e.target.value = ""; // allow re-selecting the same file
  };

  const handleSavePhoto = (dataUrl: string) => {
    setAvatar(dataUrl);
    setEditorSrc(null);
    toast.success("Profile photo updated");
  };

  const handleSaveProfile = async () => {
    if (!user?.uid) {
      toastError("Sign in to save your profile.", { flow: "account-save-profile" });
      return;
    }

    setSavingProfile(true);
    const result = await updateMyProfile({
      name: name.trim(),
      phoneNumber: phoneNumber.trim(),
      instagram: instagram.trim(),
      tShirt: tShirt.trim() || undefined,
      gender: gender === "male" || gender === "female" ? gender : undefined,
    });
    setSavingProfile(false);

    if ("error" in result) {
      toastError(result.error, { flow: "account-save-profile" });
      return;
    }

    setOriginals({ name, phoneNumber, instagram, tShirt, gender });
    toast.success("Profile saved");
  };

  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    enableBeforeUnload: () => isDirty,
    withResolver: true,
  });

  const handleDialogSave = async () => {
    await handleSaveProfile();
    blocker.proceed?.();
  };
  const handleDialogDiscard = () => {
    setName(originals.name);
    setPhoneNumber(originals.phoneNumber);
    setInstagram(originals.instagram);
    setTShirt(originals.tShirt);
    setGender(originals.gender);
    blocker.proceed?.();
  };

  const handlePasswordReset = async () => {
    if (!email) {
      toastError("No email on file", { flow: "account-reset-password-validate" });
      return;
    }
    setResettingPw(true);
    const result = await sendPasswordReset(email);
    setResettingPw(false);
    if ("success" in result) {
      toast.success(result.message ?? "If an account exists, a reset link is on the way.");
    } else {
      toastError(result.error, { flow: "account-reset-password" });
    }
  };

  const handleSignOut = async () => {
    if (dev.state !== "real") {
      dev.setState("logged-out");
      toast.success("Signed out (dev state)");
      navigate({ to: "/" });
      return;
    }
    const result = await signOut();
    if ("success" in result) {
      toast.success("Signed out");
      navigate({ to: "/" });
    } else {
      toastError(result.error, { flow: "account-sign-out" });
    }
  };

  const handleResendVerification = async () => {
    setVerifyingEmail(true);
    const result = await sendCurrentUserEmailVerification();
    setVerifyingEmail(false);
    if ("error" in result) {
      toastError(result.error, { flow: "account-send-verification" });
      return;
    }
    toast.success(result.message ?? "Verification email sent.");
  };

  const handleLinkEmailAddress = async () => {
    if (!linkEmail.trim()) {
      toastError("Please enter an email address.", {
        flow: "account-link-email-validate",
      });
      return;
    }
    if ((linkPassword?.length ?? 0) < 8) {
      toastError("Password must be at least 8 characters.", {
        flow: "account-link-email-password-length",
      });
      return;
    }
    if (linkPassword !== linkPasswordConfirm) {
      toastError("Passwords do not match.", {
        flow: "account-link-email-password-confirm",
      });
      return;
    }

    setLinkingEmail(true);
    const result = await linkEmailPassword({
      email: linkEmail,
      password: linkPassword,
    });
    setLinkingEmail(false);
    if ("error" in result) {
      toastError(result.error, { flow: "account-link-email-submit" });
      return;
    }
    toast.success(result.message ?? "Email linked.");
    setLinkPassword("");
    setLinkPasswordConfirm("");
  };

  const handleResumeSubscription = async () => {
    if (!isRealAuth) {
      toast.success("Subscription resumed.");
      return;
    }
    if (!activeSubscription?.id) {
      toastError("Could not find your active subscription.");
      return;
    }
    setResumingSubscription(true);
    const result = await callContinueSubscription({
      subscriptionId: activeSubscription.id,
    });
    setResumingSubscription(false);
    if ("error" in result) {
      toastError(result.error);
      return;
    }
    void callRefreshTokenServer()
      .then(async (refresh) => {
        if ("error" in refresh) {
          console.warn("refreshTokenServer after resume:", refresh.error);
          return;
        }
        try {
          const { getFirebaseAuth } = await import("@/lib/firebase");
          await getFirebaseAuth().currentUser?.getIdToken(true);
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        /* ignore */
      });
    toast.success(result.message || "Your subscription has been resumed.");
  };

  const handleRequestEmailChange = async () => {
    if (!newEmail.trim()) {
      toastError("Please enter a new email address.", {
        flow: "account-change-email-validate",
      });
      return;
    }
    setChangingEmail(true);
    const result = await requestEmailChange({ email: newEmail });
    setChangingEmail(false);
    if ("error" in result) {
      toastError(result.error, { flow: "account-change-email-submit" });
      return;
    }
    toast.success(result.message ?? "Email change confirmation sent.");
    setNewEmail("");
  };

  /**
   * Picks the best downloadable URL for an invoice row:
   *   - Paid invoices → Stripe receipt URL (charge.receipt_url), falling back
   *     to the invoice PDF, then the hosted invoice page.
   *   - Unpaid/open/void/draft invoices → invoice PDF, then hosted invoice
   *     page. There is no receipt to download until the invoice is paid.
   */
  const downloadUrlForInvoice = (inv: BillingInvoice): string | null => {
    if (inv.status === "paid") {
      return inv.receiptUrl ?? inv.invoicePdfUrl ?? inv.hostedInvoiceUrl ?? null;
    }
    return inv.invoicePdfUrl ?? inv.hostedInvoiceUrl ?? null;
  };

  /**
   * Opens the receipt (paid) or invoice (unpaid) PDF in a new tab. For
   * dev-state preview invoices (no real Stripe data) we surface an info toast
   * pointing the dev at the Real-auth selector. Callers shouldn't render the
   * button when no URL is available in real-auth mode; this branch is
   * defensive only.
   */
  const handleDownloadInvoice = (inv: BillingInvoice) => {
    const url = downloadUrlForInvoice(inv);
    if (!url) {
      if (!isRealAuth) {
        toast.info(
          inv.status === "paid"
            ? "Preview invoice — switch the dev selector to Real to download a real receipt."
            : "Preview invoice — switch the dev selector to Real to download a real invoice.",
        );
      }
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  /**
   * Opens the Stripe-hosted invoice page in a new tab. This is the "View"
   * affordance, distinct from the download action — the hosted page shows
   * the invoice in the browser with its own download and pay buttons.
   */
  const handleViewInvoice = (inv: BillingInvoice) => {
    const url = inv.hostedInvoiceUrl;
    if (!url) {
      if (!isRealAuth) {
        toast.info(
          "Preview invoice — switch the dev selector to Real to open a real Stripe invoice page.",
        );
      }
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleMergeAccount = async () => {
    setMergingAccount(true);
    const result = await completeDeferredMerge();
    setMergingAccount(false);
    if ("error" in result) {
      toastError(result.error, { flow: "account-complete-merge" });
      return;
    }
    toast.success(result.message ?? "Accounts merged.");
  };

  const isLoggedIn = signedIn || dev.isSignedIn;

  // ===== Logged-out state =====
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <main className="mx-auto max-w-3xl px-4 py-16">
          <div className="rounded-2xl border border-border bg-card p-10 text-center">
            <UserIcon className="mx-auto h-10 w-10 text-muted-foreground" />
            <h1 className="mt-4 text-2xl font-bold">Sign in to manage your account</h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              Your profile, membership, and payment receipts live here. Sign in or create an account
              to continue.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button asChild>
                <Link to="/membership">Become a member</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/">Back to home</Link>
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="mx-auto max-w-5xl px-4 py-8 md:py-12">
        {/* ===== Page header ===== */}
        <div className="mb-8 flex flex-col gap-2 md:mb-12">
          <span className="text-[10px] font-bold tracking-[0.25em] text-muted-foreground uppercase">
            Account
          </span>
          <h1 className="text-3xl font-bold md:text-4xl">Your account</h1>
          <p className="text-sm text-muted-foreground">
            Edit your profile, manage your membership, and download your receipts.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[2fr_3fr]">
          {/* ===== Left column: identity ===== */}
          <section className="space-y-6">
            {/* Avatar card */}
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="flex flex-col items-center text-center">
                <div className="relative">
                  <Avatar className="h-32 w-32 bg-secondary ring-2 ring-border">
                    {avatarDisplaySrc ? (
                      <AvatarImage
                        src={avatarDisplaySrc}
                        alt={name.trim() || email.trim() || "Profile"}
                        className="object-cover"
                      />
                    ) : null}
                    <AvatarFallback
                      delayMs={avatarDisplaySrc ? 500 : 0}
                      className="bg-secondary text-3xl font-semibold text-muted-foreground"
                    >
                      {profilePlaceholderInitial ? (
                        <span aria-hidden>{profilePlaceholderInitial}</span>
                      ) : (
                        <UserIcon className="h-14 w-14 opacity-80" aria-hidden />
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute right-0 bottom-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background shadow-lg transition-transform hover:scale-105"
                    aria-label="Change photo"
                  >
                    <Camera className="h-4 w-4" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFilePicked}
                  />
                </div>
                {isProfileLoading ? (
                  <Skeleton className="mt-4 h-6 w-36" />
                ) : (
                  <h2 className="mt-4 text-lg font-semibold">{name}</h2>
                )}
                {isProfileLoading ? (
                  <Skeleton className="mx-auto mt-2 h-4 w-48" />
                ) : (
                  <p className="text-sm text-muted-foreground">{email}</p>
                )}
                {showLocalDevPanel && isRealAuth && user?.uid && (
                  <LocalDevProfileDebug
                    userId={user.uid}
                    membershipType={membershipType}
                    subscriptions={subscriptions}
                  />
                )}
                {usesSocialSignIn ? (
                  <TooltipProvider delayDuration={200}>
                    <div className="mt-2 flex flex-col items-center gap-1">
                      <div className="inline-flex max-w-full items-center gap-1 rounded-full border border-border/70 bg-secondary px-2 py-1 text-[10px] font-bold tracking-wide uppercase shadow-sm">
                        <span className="flex shrink-0 items-center gap-1">
                          {hasGoogle && <GoogleGlyph className="h-3.5 w-3.5 shrink-0" />}
                          {hasApple && (
                            <AppleGlyph className="h-3.5 w-3.5 shrink-0 text-foreground" />
                          )}
                        </span>
                        <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{effectivePrimarySignInMethodLabel}</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="-my-0.5 -mr-0.5 inline-flex shrink-0 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                              aria-label="About social sign-in"
                            >
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            className="max-w-[min(22rem,calc(100vw-2rem))] border bg-popover px-3 py-2.5 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-popover-foreground shadow-lg"
                          >
                            {socialSignInTooltipBody(hasGoogle, hasApple)}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <span className="text-[10px] font-medium tracking-wide text-muted-foreground">
                        Social sign-in — hover the info icon for details
                      </span>
                    </div>
                  </TooltipProvider>
                ) : (
                  <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[10px] font-bold tracking-wide uppercase">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    {effectivePrimarySignInMethodLabel}
                  </span>
                )}
                {avatarDisplaySrc && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-3 text-xs"
                    onClick={() => {
                      setEditorSrc(avatarDisplaySrc);
                    }}
                  >
                    Adjust photo
                  </Button>
                )}
              </div>
            </div>

            {/* Email + password card */}
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="text-sm font-bold tracking-wide uppercase text-muted-foreground">
                Login &amp; security
              </h3>

              <div className="mt-4 space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm">
                    {isProfileLoading ? (
                      <Skeleton className="h-4 w-56" />
                    ) : (
                      <>
                        <span className="flex items-center gap-2 truncate">
                          <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate">{email || "No email on file"}</span>
                        </span>
                        {!email ? (
                          <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-bold tracking-wide text-muted-foreground uppercase">
                            Phone-only
                          </span>
                        ) : emailVerified ? (
                          <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-bold tracking-wide text-[oklch(0.62_0.19_250)] uppercase">
                            <BadgeCheck className="h-3.5 w-3.5" />
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-bold tracking-wide text-amber-500 uppercase">
                            <ShieldAlert className="h-3.5 w-3.5" />
                            Unverified
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {!email && dev.state === "real" && (
                    <div className="mt-3 space-y-2 rounded-md border border-border bg-background p-3">
                      <p className="text-xs text-muted-foreground">
                        Add an email address so you can sign in with phone or email.
                      </p>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        value={linkEmail}
                        onChange={(e) => setLinkEmail(e.target.value)}
                      />
                      <Input
                        type="password"
                        placeholder="Create password (min 8 chars)"
                        value={linkPassword}
                        onChange={(e) => setLinkPassword(e.target.value)}
                      />
                      <Input
                        type="password"
                        placeholder="Confirm password"
                        value={linkPasswordConfirm}
                        onChange={(e) => setLinkPasswordConfirm(e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-start gap-2"
                        onClick={handleLinkEmailAddress}
                        disabled={linkingEmail}
                      >
                        {linkingEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        Add email and send verification
                      </Button>
                    </div>
                  )}
                  {!!email && !emailVerified && (
                    <button
                      type="button"
                      className="mt-2 text-xs font-semibold text-[oklch(0.62_0.19_250)] hover:underline"
                      onClick={handleResendVerification}
                      disabled={verifyingEmail}
                    >
                      {verifyingEmail ? "Sending verification..." : "Resend verification email"}
                    </button>
                  )}
                  {!!email && dev.state === "real" && !usesSocialSignIn && (
                    <div className="mt-3 space-y-2 rounded-md border border-border bg-background p-3">
                      <p className="text-xs text-muted-foreground">
                        Change email securely: we send a confirmation link to the new address.
                      </p>
                      <Input
                        type="email"
                        placeholder="new-email@example.com"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-start gap-2"
                        onClick={handleRequestEmailChange}
                        disabled={changingEmail}
                      >
                        {changingEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        Send email change confirmation
                      </Button>
                    </div>
                  )}
                  {!!email && dev.state === "real" && usesSocialSignIn && (
                    <p className="mt-3 rounded-md border border-dashed border-border/80 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                      Email changes aren&apos;t available for social sign-in. Update your email or
                      linked addresses in{" "}
                      {hasGoogle && hasApple ? "Google and Apple" : hasGoogle ? "Google" : "Apple"}{" "}
                      when you need to change the address tied to this account.
                    </p>
                  )}
                </div>

                {(!usesSocialSignIn || effectiveHasPasswordAuth) && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-xs text-muted-foreground">Password</Label>
                      {effectiveHasPasswordAuth ? (
                        <Button
                          type="button"
                          variant="outline"
                          className="mt-2 w-full justify-start gap-2"
                          onClick={handlePasswordReset}
                          disabled={resettingPw}
                        >
                          {resettingPw ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <KeyRound className="h-4 w-4" />
                          )}
                          Send password reset email
                        </Button>
                      ) : (
                        <p className="mt-2 rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                          This account uses {effectivePrimarySignInMethodLabel} sign-in, so password
                          reset is not available.
                        </p>
                      )}
                    </div>
                  </>
                )}

                {dev.state === "real" && pendingAccountMerge && effectiveHasPasswordAuth && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-xs text-muted-foreground">Account merge</Label>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Merge pending for {pendingAccountMerge.email}. Link{" "}
                        {pendingAccountMerge.providerId === "google.com" ? "Google" : "Apple"} to
                        this account.
                      </p>
                      <div className="mt-2 flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="justify-start gap-2"
                          onClick={handleMergeAccount}
                          disabled={mergingAccount}
                        >
                          {mergingAccount ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Link2 className="h-4 w-4" />
                          )}
                          Merge now
                        </Button>
                        <Button type="button" variant="ghost" onClick={clearAccountMerge}>
                          Not now
                        </Button>
                      </div>
                    </div>
                  </>
                )}

                {dev.state === "real" &&
                  effectiveHasPasswordAuth &&
                  !pendingAccountMerge &&
                  !signInMethods.includes("google.com") && (
                    <>
                      <Separator />
                      <p className="text-xs text-muted-foreground">
                        Tip: You can add Google sign-in by clicking "Sign in with Google" on the
                        login page, then choosing merge.
                      </p>
                    </>
                  )}
                <Separator />
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={handleSignOut}
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </Button>
              </div>
            </div>
          </section>

          {/* ===== Right column: profile + membership + billing ===== */}
          <section className="space-y-6">
            {/* Profile fields */}
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="text-sm font-bold tracking-wide uppercase text-muted-foreground">
                Profile
              </h3>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {isProfileLoading ? (
                  <>
                    <ProfileFieldSkeleton label="Name" className="sm:col-span-2" />
                    <ProfileFieldSkeleton label="Phone number" />
                    <ProfileFieldSkeleton label="Instagram" />
                    <ProfileFieldSkeleton label="T-shirt size" />
                    <ProfileFieldSkeleton label="Gender" />
                  </>
                ) : (
                  <>
                <div className="sm:col-span-2">
                  <Label htmlFor="name">
                    Name
                    {dirtyFields.name && (
                      <span className="ml-2 text-xs font-medium text-amber-500">Unsaved</span>
                    )}
                  </Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={80}
                    className={cn("mt-1", dirtyFields.name && unsavedInputClass)}
                  />
                </div>

                <div>
                  <Label htmlFor="phone">
                    Phone number
                    {dirtyFields.phoneNumber && (
                      <span className="ml-2 text-xs font-medium text-amber-500">Unsaved</span>
                    )}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 (555) 010-0000"
                    maxLength={32}
                    className={cn("mt-1", dirtyFields.phoneNumber && unsavedInputClass)}
                  />
                </div>

                <div>
                  <Label htmlFor="instagram">
                    Instagram
                    {dirtyFields.instagram && (
                      <span className="ml-2 text-xs font-medium text-amber-500">Unsaved</span>
                    )}
                  </Label>
                  <Input
                    id="instagram"
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value)}
                    placeholder="@yourhandle"
                    maxLength={32}
                    className={cn("mt-1", dirtyFields.instagram && unsavedInputClass)}
                  />
                </div>

                <div>
                  <Label htmlFor="tshirt">
                    T-shirt size
                    {dirtyFields.tShirt && (
                      <span className="ml-2 text-xs font-medium text-amber-500">Unsaved</span>
                    )}
                  </Label>
                  <Select
                    value={tShirt && T_SHIRT_SIZES.includes(tShirt) ? tShirt : T_SHIRT_UNSET}
                    onValueChange={(v) => setTShirt(v === T_SHIRT_UNSET ? "" : v)}
                  >
                    <SelectTrigger
                      id="tshirt"
                      className={cn("mt-1", dirtyFields.tShirt && unsavedInputClass)}
                    >
                      <SelectValue placeholder="Select a size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={T_SHIRT_UNSET}>Not set</SelectItem>
                      {T_SHIRT_SIZES.map((size) => (
                        <SelectItem key={size} value={size}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="gender">
                    Gender
                    {dirtyFields.gender && (
                      <span className="ml-2 text-xs font-medium text-amber-500">Unsaved</span>
                    )}
                  </Label>
                  <Select
                    value={gender === "male" || gender === "female" ? gender : undefined}
                    onValueChange={(v) => setGender(v)}
                  >
                    <SelectTrigger
                      id="gender"
                      className={cn("mt-1", dirtyFields.gender && unsavedInputClass)}
                    >
                      <SelectValue placeholder="Not set" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                  </>
                )}
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                {isDirty && !isProfileLoading && (
                  <span className="text-xs text-amber-500">You have unsaved changes</span>
                )}
                <Button
                  onClick={handleSaveProfile}
                  disabled={isProfileLoading || savingProfile || !isDirty}
                >
                  {savingProfile && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save changes
                </Button>
              </div>
            </div>

            {SHOW_ACCOUNT_GIVEAWAYS && isRealAuth && signedIn && user?.uid && (
              <AccountGiveawaysCard userId={user.uid} />
            )}

            {/* Membership */}
            <div className="rounded-2xl border border-border bg-card p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-bold tracking-wide uppercase text-muted-foreground">
                    Membership
                  </h3>
                  <div className="mt-2 flex items-center gap-2">
                    {dev.isSubscribed || isSubscriber ? (
                      <>
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[oklch(0.62_0.19_250)]/15 text-[oklch(0.62_0.19_250)]">
                          <Crown className="h-4 w-4" />
                        </span>
                        <span className="text-lg font-bold">{membershipLabel}</span>
                      </>
                    ) : (
                      <>
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                          <UserIcon className="h-4 w-4" />
                        </span>
                        <span className="text-lg font-bold">Free</span>
                      </>
                    )}
                  </div>
                </div>
                {(dev.isSubscribed || isSubscriber) && (
                  <span
                    className={cn(
                      "inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-[10px] font-bold tracking-[0.2em] uppercase",
                      isCancellationScheduled
                        ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                        : "bg-[oklch(0.62_0.19_250)]/15 text-[oklch(0.62_0.19_250)]",
                    )}
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    {isCancellationScheduled
                      ? nextRenewal
                        ? `Cancels ${formatDate(nextRenewal)}`
                        : "Cancelling"
                      : "Active"}
                  </span>
                )}
              </div>

              {membershipSummary ? (
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {membershipSummary}
                </p>
              ) : null}

              {pendingLibraryOnlySwitch && nextRenewal ? (
                <p className="mt-3 text-sm leading-relaxed text-[oklch(0.62_0.19_250)]">
                  Switching to <strong>Library Only</strong> on{" "}
                  {formatLongDate(nextRenewal)} at{" "}
                  <strong>${libraryOnlyPriceUsd.toFixed(2)}/month</strong>. You&apos;ll
                  keep your current videos with no new weekly drops after that date.
                </p>
              ) : null}

              {isCancellationScheduled && (
                <p className="mt-3 text-sm leading-relaxed text-amber-600 dark:text-amber-400">
                  Your membership is scheduled to cancel
                  {nextRenewal ? ` on ${formatLongDate(nextRenewal)}` : ""}. You
                  keep access until then and will not be charged again unless you
                  resume.
                </p>
              )}

              {dev.isSubscribed || isSubscriber ? (
                <>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    <Stat
                      label="Subscribed"
                      value={formatRelativeWeeks(subscribedWeeks)}
                      hint={subscribedSince ? `Since ${formatDate(subscribedSince)}` : undefined}
                    />
                    <Stat
                      label={isCancellationScheduled ? "Access until" : "Next renewal"}
                      value={nextRenewal ? formatDate(nextRenewal) : "—"}
                      hint={
                        isCancellationScheduled
                          ? "You won't be charged again"
                          : `$${planPriceUsd.toFixed(2)} / month`
                      }
                    />
                  </div>

                  {subscribedWeeks >= 52 && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg bg-[oklch(0.62_0.19_250)]/10 px-3 py-2 text-sm text-[oklch(0.62_0.19_250)]">
                      <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        Thanks for sticking around for{" "}
                        <strong>{formatRelativeWeeks(subscribedWeeks)}</strong>. You&apos;re part of
                        our long-haul crew.
                      </span>
                    </div>
                  )}

                  {isLegacyMembership && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-[oklch(0.62_0.19_250)]/40 bg-[oklch(0.62_0.19_250)]/5 px-3 py-3 text-sm">
                      <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[oklch(0.62_0.19_250)]" />
                      <div>
                        <p className="font-semibold text-foreground">OG Legacy subscriber</p>
                        <p className="mt-1 text-muted-foreground">
                          You have full catalog access on your OG Legacy plan. Switching or
                          cancelling changes what you keep — see{" "}
                          <Link to="/membership" className="font-semibold text-[oklch(0.62_0.19_250)] hover:underline">
                            Change plan
                          </Link>{" "}
                          for details before making changes.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-5 flex flex-wrap gap-2">
                    <Button asChild variant="outline" className="gap-2">
                      <Link to="/membership">
                        <Crown className="h-4 w-4" />
                        Change plan
                      </Link>
                    </Button>
                    {superSubAccess ? (
                      <Button asChild className="gap-2">
                        <Link to="/vip-chat">Open VIP Chat</Link>
                      </Button>
                    ) : null}
                    <Button variant="outline" className="gap-2" type="button">
                      <CreditCard className="h-4 w-4" />
                      Update payment info
                    </Button>
                    {isCancellationScheduled ? (
                      <Button
                        variant="default"
                        className="gap-2"
                        type="button"
                        disabled={resumingSubscription}
                        onClick={() => void handleResumeSubscription()}
                      >
                        {resumingSubscription && (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        )}
                        Resume subscription
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        className="gap-2 text-muted-foreground"
                        type="button"
                        onClick={() => setCancelFlowOpen(true)}
                      >
                        Cancel subscription
                      </Button>
                    )}
                  </div>

                  {superSubAccess ? (
                    <div className="mt-5 space-y-3">
                      <h4 className="text-sm font-bold tracking-wide uppercase text-muted-foreground">
                        Super Sub VIP Group
                      </h4>
                      <VipTelegramConnectBanner userId={user?.uid} />
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">
                    You don&apos;t have an active membership. Subscribe to unlock every weekly drop
                    and grow your library.
                  </p>
                  <Button asChild className="mt-4 gap-2">
                    <Link to="/membership">
                      <Crown className="h-4 w-4" />
                      See plans
                    </Link>
                  </Button>
                </div>
              )}
            </div>

            {(dev.isSubscribed || isSubscriber) && !isLegacyMembership && !isLibraryOnlyMember && (
              <DropNotificationPreferences />
            )}

            {/* Billing history */}
            {showBilling && (
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-sm font-bold tracking-wide uppercase text-muted-foreground">
                    Payment history
                  </h3>
                  {!billingIsLoading && !invoicesError && billing.invoices.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {billingExpanded
                        ? `Showing ${billingPage * BILLING_PAGE_SIZE + 1}–${Math.min(billing.invoices.length, billingPage * BILLING_PAGE_SIZE + BILLING_PAGE_SIZE)} of ${billing.totalMonths}`
                        : `Showing last ${Math.min(BILLING_COLLAPSED, billing.invoices.length)} of ${billing.totalMonths}`}
                    </span>
                  )}
                </div>

                {billingIsLoading ? (
                  <ul className="mt-4 divide-y divide-border">
                    {Array.from({ length: BILLING_COLLAPSED }).map((_, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 py-3">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-44" />
                            <Skeleton className="h-3 w-56" />
                          </div>
                        </div>
                        <Skeleton className="h-7 w-16 rounded-full" />
                      </li>
                    ))}
                  </ul>
                ) : invoicesError ? (
                  <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
                    <p className="font-semibold text-destructive">
                      Couldn&apos;t load your payment history.
                    </p>
                    <p className="mt-1 text-muted-foreground">{invoicesError}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={reloadInvoices}
                    >
                      Try again
                    </Button>
                  </div>
                ) : billingIsEmpty ? (
                  <div className="mt-4 rounded-md border border-dashed border-border bg-background/40 p-6 text-center">
                    <Receipt className="mx-auto h-6 w-6 text-muted-foreground" />
                    <p className="mt-2 text-sm font-semibold">No receipts yet</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Your first invoice will appear here after your next renewal.
                    </p>
                  </div>
                ) : (
                  <ul className="mt-4 divide-y divide-border">
                    {billingVisible.map((inv) => {
                      const isPaid = inv.status === "paid";
                      const isPayPal = inv.paymentMethod === "paypal";
                      const downloadUrl = downloadUrlForInvoice(inv);
                      const downloadable = !!downloadUrl;
                      const viewable = !!inv.hostedInvoiceUrl;
                      // Dev-state preview rows never carry real URLs; keep the
                      // buttons visible there so the affordances still mock up.
                      // Real Stripe invoices without a URL (drafts the server
                      // didn't filter, or anything truly un-downloadable) get
                      // the button hidden so we never show a clickable control
                      // that just errors.
                      const showDownloadButton = downloadable || !isRealAuth;
                      const showViewButton = viewable || !isRealAuth;
                      const description = inv.description?.trim() || "Monthly membership";
                      const downloadLabel = isPaid
                        ? `Download receipt ${inv.number}`
                        : `Download invoice ${inv.number}`;
                      const downloadTitle = isPaid
                        ? downloadable
                          ? "Download receipt"
                          : "Preview only"
                        : downloadable
                          ? "Download invoice"
                          : "Preview only";
                      return (
                        <li
                          key={inv.id}
                          className="flex items-center justify-between gap-3 py-3"
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={cn(
                                "inline-flex h-8 w-8 items-center justify-center rounded-full",
                                isPayPal
                                  ? "bg-[#003087]/10 text-[#003087]"
                                  : "bg-secondary text-muted-foreground",
                              )}
                              aria-label={
                                isPayPal ? "Paid with PayPal" : undefined
                              }
                              title={isPayPal ? "Paid with PayPal" : undefined}
                            >
                              {isPayPal ? (
                                <PayPalGlyph className="h-4 w-4" />
                              ) : (
                                <Receipt className="h-4 w-4" />
                              )}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold">
                                {formatMoney(inv.amountCents, inv.currency)} · {description}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatLongDate(inv.date)} · {inv.number}
                                {isPayPal && (
                                  <>
                                    {" · "}
                                    <span className="inline-flex items-center gap-1 font-medium text-[#003087] dark:text-[#5BA9F5]">
                                      <PayPalGlyph className="h-3 w-3" />
                                      PayPal
                                    </span>
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                "rounded-full px-2 py-0.5 text-[10px] font-bold tracking-[0.15em] uppercase",
                                isPaid
                                  ? "bg-[oklch(0.62_0.19_250)]/15 text-[oklch(0.62_0.19_250)]"
                                  : inv.status === "open"
                                    ? "bg-amber-500/15 text-amber-500"
                                    : inv.status === "void" || inv.status === "uncollectible"
                                      ? "bg-destructive/15 text-destructive"
                                      : "bg-secondary text-muted-foreground",
                              )}
                            >
                              {inv.status}
                            </span>
                            {showViewButton && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "h-8 gap-1.5 px-2.5",
                                  !viewable && "text-muted-foreground/60",
                                )}
                                aria-label={`View invoice ${inv.number} on Stripe`}
                                title={viewable ? "View on Stripe" : "Preview only"}
                                onClick={() => handleViewInvoice(inv)}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">View</span>
                              </Button>
                            )}
                            {showDownloadButton && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={cn(
                                  "h-8 gap-1.5 px-2.5",
                                  !downloadable && "text-muted-foreground/60",
                                )}
                                aria-label={downloadLabel}
                                title={downloadTitle}
                                onClick={() => handleDownloadInvoice(inv)}
                              >
                                <Download className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">
                                  {isPaid ? "Receipt" : "Invoice"}
                                </span>
                              </Button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {!billingIsLoading && !invoicesError && billing.invoices.length > BILLING_COLLAPSED && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => {
                        setBillingExpanded((v) => !v);
                        setBillingPage(0);
                      }}
                    >
                      {billingExpanded ? (
                        <>
                          <ChevronUp className="h-4 w-4" /> Collapse
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4" /> Show more
                        </>
                      )}
                    </Button>

                    {billingExpanded && billingPageCount > 1 && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border transition-colors hover:bg-secondary disabled:opacity-40"
                          onClick={() => setBillingPage((p) => Math.max(0, p - 1))}
                          disabled={billingPage === 0}
                          aria-label="Previous page"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <span>
                          Page {billingPage + 1} of {billingPageCount}
                        </span>
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border transition-colors hover:bg-secondary disabled:opacity-40"
                          onClick={() =>
                            setBillingPage((p) => Math.min(billingPageCount - 1, p + 1))
                          }
                          disabled={billingPage >= billingPageCount - 1}
                          aria-label="Next page"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      </main>

      <AvatarEditor
        open={editorSrc !== null}
        src={editorSrc}
        onCancel={() => setEditorSrc(null)}
        onSave={handleSavePhoto}
      />

      <AlertDialog
        open={blocker.status === "blocked"}
        onOpenChange={(open) => {
          if (!open) blocker.reset?.();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved profile changes. Do you want to save them before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel onClick={() => blocker.reset?.()}>
              Keep editing
            </AlertDialogCancel>
            <Button variant="secondary" onClick={handleDialogDiscard} disabled={savingProfile}>
              Discard changes
            </Button>
            <AlertDialogAction onClick={handleDialogSave} disabled={savingProfile}>
              {savingProfile && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save and continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CancelMembershipDialog
        open={cancelStep !== "closed"}
        step={cancelStep}
        onStepChange={setCancelStep}
        onClose={closeCancelFlow}
        isRealAuth={isRealAuth}
        isLegacySubscriber={isLegacyMembership}
        planPriceUsd={planPriceUsd}
        libraryOnlyPriceUsd={libraryOnlyPriceUsd}
        videosOwned={videosOwned}
        subscriptions={subscriptions}
      />
    </div>
  );
}

function GoogleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.29h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.54-5.16 3.54-8.65Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.07 7.95-2.91l-3.88-3c-1.08.73-2.45 1.17-4.07 1.17-3.13 0-5.78-2.11-6.73-4.96H1.27v3.09A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.3A7.2 7.2 0 0 1 4.9 12c0-.8.14-1.58.37-2.3V6.61H1.27A12 12 0 0 0 0 12c0 1.94.46 3.77 1.27 5.39l4-3.09Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.74c1.76 0 3.35.61 4.59 1.8l3.44-3.44C17.95 1.13 15.24 0 12 0A12 12 0 0 0 1.27 6.61l4 3.09c.95-2.85 3.6-4.96 6.73-4.96Z"
      />
    </svg>
  );
}

function AppleGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M16.37 1.43c0 1.14-.42 2.22-1.21 3.04-.84.88-2.1 1.55-3.27 1.46-.13-1.13.43-2.32 1.18-3.05.83-.81 2.23-1.42 3.3-1.45ZM20.5 17.05c-.55 1.27-.81 1.83-1.52 2.95-.99 1.55-2.39 3.49-4.13 3.5-1.55.02-1.95-1-4.05-1-2.1.01-2.54 1.02-4.09 1-1.74-.02-3.06-1.77-4.05-3.32C-.18 16.83-.5 11.66 1.84 8.93c1.18-1.39 3.05-2.27 4.85-2.27 1.83 0 2.98 1 4.49 1 1.46 0 2.36-1 4.47-1 1.59 0 3.27.87 4.47 2.36-3.93 2.16-3.29 7.78.38 8.03Z" />
    </svg>
  );
}

/**
 * Stylized PayPal "PP" glyph used as the brand marker in the payment history
 * list. Two overlapping `P`s in PayPal blue tones — readable at the 14×14 px
 * size we render inside the receipt avatar circle. Decorative only; the row
 * label carries the actual `PayPal` text for screen readers.
 */
function PayPalGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill="#003087"
        d="M7.27 21.5H4.7c-.27 0-.48-.24-.43-.5l3-15.5c.04-.2.21-.34.42-.34h5.46c2.96 0 4.96 1.45 4.96 4.07 0 3.2-2.42 5.43-6.07 5.43H9.18a.43.43 0 0 0-.42.35l-1.07 5.97a.43.43 0 0 1-.42.52Z"
      />
      <path
        fill="#009CDE"
        d="M11.55 18.5H8.98c-.27 0-.48-.24-.43-.5l3-15.5c.04-.2.21-.34.42-.34h5.46c2.96 0 4.96 1.45 4.96 4.07 0 3.2-2.42 5.43-6.07 5.43h-2.86a.43.43 0 0 0-.42.35l-1.07 5.97a.43.43 0 0 1-.42.52Z"
      />
    </svg>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="text-[10px] font-bold tracking-[0.2em] text-muted-foreground uppercase">
        {label}
      </p>
      <p className="mt-1 text-lg font-bold">{value}</p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
